import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, CheckCircle2, XCircle, Clock, Eye, Mail, Building } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const STATUS_META = {
  pending:   { label: "En attente",   color: "bg-orange-100 text-orange-700 border-orange-200", icon: Clock },
  in_review: { label: "En cours",     color: "bg-blue-100 text-blue-700 border-blue-200",       icon: Eye },
  resolved:  { label: "Traitée",      color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  rejected:  { label: "Refusée",      color: "bg-gray-100 text-gray-700 border-gray-200",       icon: XCircle },
};

const TYPE_LABEL = {
  modification: "Modification",
  suppression:  "Suppression",
  partenariat:  "Partenariat",
  autre:        "Autre",
};

export default function AdminCorrections() {
  const { logout } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = () => api.get("/admin/company-corrections").then((r) => setItems(r.data || []));
  useEffect(() => { load(); document.title = "Demandes de correction — Admin"; }, []);

  const filtered = filter === "all" ? items : items.filter((x) => x.status === filter);
  const counts = items.reduce((acc, x) => { acc[x.status] = (acc[x.status] || 0) + 1; return acc; }, {});

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/company-corrections/${id}`, { status, admin_notes: notes });
    setNotes("");
    setSelected(null);
    load();
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="bg-white border-b border-[#1C1C1C]">
        <div className="container mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="eyebrow bg-[#1D3557] text-white px-2 py-1">Admin</span>
            <h1 className="font-heading font-black text-xl tracking-tight">Demandes de correction</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-sm font-semibold hover:text-[#E07A5F]" data-testid="back-admin">
              <ArrowLeft className="w-4 h-4 inline" /> Dashboard
            </Link>
            <button onClick={logout} className="text-sm font-semibold hover:text-[#C44536]">Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-10">
        {/* Stat blocks by status */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <StatCard label="Toutes" count={items.length} active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all" />
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <StatCard
              key={key}
              label={meta.label}
              count={counts[key] || 0}
              active={filter === key}
              onClick={() => setFilter(key)}
              icon={meta.icon}
              testid={`filter-${key}`}
            />
          ))}
        </div>

        <div className="bg-white border border-[#1C1C1C] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EFE9]">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Type</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Entreprise</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Demandeur</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Reçue le</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Statut</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-[#6B7280]">Aucune demande.</td></tr>
              )}
              {filtered.map((c) => {
                const M = STATUS_META[c.status] || STATUS_META.pending;
                return (
                  <tr key={c.id} className="border-t border-[#E5E7EB] hover:bg-[#F7F9FB]" data-testid={`correction-row-${c.id}`}>
                    <td className="px-4 py-3 font-semibold text-xs uppercase tracking-widest">{TYPE_LABEL[c.request_type] || c.request_type}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{c.company_name}</div>
                      {c.siret && <div className="text-[10px] text-[#6B7280]">SIRET {c.siret}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-medium">{c.requester_first_name} {c.requester_last_name}</div>
                      <div className="text-[#6B7280]">{c.requester_role || "—"}</div>
                      <a href={`mailto:${c.requester_email}`} className="text-[#1AA66C] hover:underline flex items-center gap-1 mt-0.5">
                        <Mail className="w-3 h-3" /> {c.requester_email}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280]">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${M.color}`}>
                        <M.icon className="w-3 h-3" /> {M.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {c.company_slug && (
                        <a href={`/demenageur/${c.company_slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#4285F4] hover:underline mr-3 inline-flex items-center gap-1">
                          Fiche <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <button onClick={() => { setSelected(c); setNotes(c.admin_notes || ""); }} className="text-xs uppercase tracking-widest hover:text-[#2BD391]" data-testid={`view-correction-${c.id}`}>
                        Détail
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Detail modal */}
        {selected && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
            <div className="bg-white rounded-2xl max-w-2xl w-full p-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="correction-detail">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-1">
                    Demande — {TYPE_LABEL[selected.request_type]}
                  </div>
                  <h2 className="font-heading font-extrabold text-2xl text-[#2C3E50] flex items-center gap-2">
                    <Building className="w-5 h-5 text-[#2BD391]" />
                    {selected.company_name}
                  </h2>
                </div>
                <button onClick={() => setSelected(null)} className="text-2xl text-[#6B7280] hover:text-[#2C3E50]">×</button>
              </div>

              <div className="space-y-4 text-sm mb-6">
                {selected.siret && <Row k="SIRET" v={selected.siret} />}
                <Row k="Demandeur" v={`${selected.requester_first_name} ${selected.requester_last_name}${selected.requester_role ? ` — ${selected.requester_role}` : ""}`} />
                <Row k="Email" v={<a href={`mailto:${selected.requester_email}`} className="text-[#1AA66C] hover:underline">{selected.requester_email}</a>} />
                <Row k="Message" v={<p className="whitespace-pre-wrap text-[#2C3E50]">{selected.message}</p>} />
                {selected.justification && <Row k="Justificatif" v={<p className="whitespace-pre-wrap text-[#6B7280] text-xs italic">{selected.justification}</p>} />}
              </div>

              <div className="border-t border-[#F0F2F5] pt-5">
                <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">Notes internes</label>
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="input-square !rounded-2xl w-full" data-testid="correction-notes" />

                <div className="mt-5 flex flex-wrap gap-2">
                  <button onClick={() => updateStatus(selected.id, "in_review")} className="btn-outline text-xs" data-testid="mark-review">
                    <Eye className="w-3.5 h-3.5" /> En cours
                  </button>
                  <button onClick={() => updateStatus(selected.id, "resolved")} className="btn-primary text-xs" data-testid="mark-resolved">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Traitée
                  </button>
                  <button onClick={() => updateStatus(selected.id, "rejected")} className="btn-outline text-xs !border-red-300 !text-red-700 hover:!bg-red-50" data-testid="mark-rejected">
                    <XCircle className="w-3.5 h-3.5" /> Refuser
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ label, count, active, onClick, icon: Icon, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-left p-4 rounded-2xl border transition-all ${active ? "bg-[#2C3E50] text-white border-[#2C3E50]" : "bg-white border-[#E5E7EB] hover:border-[#2BD391] text-[#2C3E50]"}`}
  >
    <div className="text-xs uppercase tracking-widest font-bold opacity-80 flex items-center gap-1.5">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </div>
    <div className="font-heading font-black text-3xl mt-1">{count}</div>
  </button>
);

const Row = ({ k, v }) => (
  <div className="grid grid-cols-3 gap-3">
    <div className="text-xs uppercase tracking-widest text-[#6B7280] font-bold">{k}</div>
    <div className="col-span-2">{v}</div>
  </div>
);
